import { useState, type KeyboardEvent, type SyntheticEvent } from "react";

type Args = {
  values: string[];
  onChange: (values: string[]) => void;
  max: number;
  maxMessage: string;
  validate?: (value: string, nextValues: string[]) => string | null;
};

export const useTagListInput = ({ values, onChange, max, maxMessage, validate }: Args) => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const atLimit = values.length >= max;
  const isDraftEmpty = draft.trim().length === 0;

  const add = (event?: SyntheticEvent) => {
    event?.preventDefault();

    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft("");
      return;
    }

    if (atLimit) {
      setError(maxMessage);
      return;
    }

    const normalized = trimmed.toLowerCase();
    if (values.some((value) => value.trim().toLowerCase() === normalized)) {
      setDraft("");
      setError(null);
      return;
    }

    const nextValues = values.concat([trimmed]);
    const validationError = validate?.(trimmed, nextValues) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }

    onChange(nextValues);
    setDraft("");
    setError(null);
  };

  const remove = (value: string) => {
    onChange(values.filter((existing) => existing !== value));
    setError(null);
  };

  const changeDraft = (value: string) => {
    setDraft(value);
    setError(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      add(event);
    }
  };

  return {
    draft,
    changeDraft,
    error,
    setError,
    add,
    remove,
    onKeyDown,
    atLimit,
    isDraftEmpty,
  };
};

export type TagListInput = ReturnType<typeof useTagListInput>;
